<template>
  <div>
    <v-progress-circular v-if="isLoading" indeterminate color="secondary" size="15" />

    <key-value-list v-else>
      <div v-for="assetContainerField in assetContainerFields" :key="assetContainerField.field">
        <key-value :label="assetContainerField.label">
          <template #value>
            <div
              v-if="!assetContainerField.isEditing"
              :data-testid="assetContainerField.testId"
              class="d-flex flex-row align-center justify-between custom-form-field">
              <span v-html="assetContainerField.value"></span>
              <v-spacer />
              <v-btn
                v-if="isEditable"
                :title="t('yard.tooltips.editData')"
                variant="plain"
                icon="mdi-pencil"
                size="small"
                class="edit-button"
                @click="assetContainerField.isEditing = true" />
            </div>

            <v-form ref="form" v-else>
              <component
                :is="customFormsComponentsMapping[assetContainerField.type].component"
                :label="null"
                variant="outlined"
                v-model="assetContainerField.value"
                :dropdown-values="assetContainerField.dropDownValues"
                :field="assetContainerField" />

              <div class="d-flex align-center gap-2 mb-6 mt-4">
                <v-btn
                  size="small"
                  variant="outlined"
                  @click="() => (assetContainerField.isEditing = false)">
                  {{ t('global.buttons.nevermind') }}
                </v-btn>
                <v-btn
                  size="small"
                  class="bg-primary"
                  @click="() => handleSave(assetContainerField)"
                  :disabled="!assetContainerField.value">
                  {{ t('global.buttons.save') }}
                </v-btn>
              </div>
            </v-form>
          </template>
        </key-value>
      </div>

      <custom-forms-multiple-trigger-form
        v-if="triggers.length"
        :warehouse-id="warehouse.id"
        :is-editable="true"
        :object-id="assetContainer.id"
        :triggers="triggers"
        :timezone="warehouse.timezone"
        :components-mapping="customFormsComponentsMapping"
        @update:form-field="handleFormFieldUpdate"
        :military-time-enabled="$isMilitaryTimeEnabled(warehouse)" />

      <yard-view-asset-container-notes :asset-container="assetContainer" />
    </key-value-list>
  </div>
</template>
<script>
import { defineComponent, ref } from 'vue';
import CustomFormsMultipleTriggerForm from '@satellite/components/elements/CustomFormsMultipleTriggerForm.vue';
import YardViewAssetContainerNotes from '@/modules/yard/components/AssetContainer/YardViewAssetContainerNotes.vue';
import useYard from '@/modules/yard/composables/useYard';
import { useCustomForms, useNotify } from '@/composables';
import { AssetContainerType, CustomFieldType } from '@nova/core';
import KeyValueList from '@satellite/components/elements/key_value/KeyValueList.vue';
import KeyValue from '@satellite/components/elements/key_value/KeyValue.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAssetContainerFormData',
  components: {
    CustomFormsMultipleTriggerForm,
    YardViewAssetContainerNotes,
    KeyValueList,
    KeyValue
  },
  emits: ['update:formField'],
  props: {
    assetContainer: {
      type: Object,
      required: true
    },
    isEditable: {
      type: Boolean,
      required: false,
      default: false
    },
    warehouse: {
      type: Object,
      required: true
    },
    triggers: {
      type: Array,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const notify = useNotify();

    const isLoading = ref(false);

    const { customFormsComponentsMapping } = useCustomForms();

    const assetContainerFields = ref([
      {
        field: 'code',
        label: t('yard.pages.yardView.headers.assetId'),
        testId: 'asset-details-code-field',
        isEditing: false,
        type: CustomFieldType.String,
        value: props.assetContainer.code
      },
      {
        field: 'type',
        label: t('yard.pages.yardView.headers.assetType'),
        testId: 'asset-details-type-field',
        isEditing: false,
        type: CustomFieldType.DropDown,
        value: props.assetContainer.type,
        dropDownValues: Object.values(AssetContainerType)
      }
    ]);

    const handleSave = assetContainerField => {
      isLoading.value = true;

      yardStore
        .updateAssetContainer(props.assetContainer.id, {
          [assetContainerField.field]: assetContainerField.value
        })
        .then(() => {
          assetContainerField.isEditing = false;
          yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.UPDATE_ASSET_CONTAINER, {
            entryPoint: 'Asset Container Form',
            spot: yardStore.selectedSpotRef.value,
            assetVisit: props.assetContainer,
            metadata: { field: assetContainerField.field }
          });
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    const handleFormFieldUpdate = formField => {
      notify('Form Field updated successfully', 'success');
      yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.UPDATE_FORM_FIELD, {
        entryPoint: 'Asset Container Form',
        spot: yardStore.selectedSpotRef.value,
        assetVisit: props.assetContainer,
        metadata: { field: formField?.label }
      });
    };

    return {
      t,
      assetContainerFields,
      customFormsComponentsMapping,
      handleFormFieldUpdate,
      isLoading,
      handleSave
    };
  }
});
</script>
<style lang="scss">
.custom-form-field {
  &:hover {
    .edit-button {
      opacity: 1;
    }
  }

  .edit-button {
    opacity: 0;
    padding: 0;
    background-color: $white;
    margin-left: 4px;
    transition: opacity 0.3s ease-in-out;
  }
}
</style>
